import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, apps } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const article = articles.find((a) => a.id === Number(id));
  const relatedApps = article ? apps.filter((app) => app.articleId === article.id) : [];
  const relatedArticles = article
    ? articles.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3)
    : [];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | 体育App指南`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', article.excerpt);
      }
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-6 py-20 text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-accent-600 hover:text-accent-700 text-sm whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '体育App指南',
    },
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-6 md:px-10">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mt-8 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-accent-600 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.category}`} className="hover:text-accent-600 transition-colors whitespace-nowrap">
              {article.category === 'saishi-zhibo' ? '赛事直播' : article.category === 'yundong-jilu' ? '运动记录' : article.category === 'jingcai-hudong' ? '竞猜互动' : '体育资讯'}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
          </nav>

          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 bg-accent-50 text-accent-700 rounded-md text-xs whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-snug">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500 flex-wrap">
              <span className="whitespace-nowrap">{article.author}</span>
              <span className="whitespace-nowrap">{article.date}</span>
              <span className="whitespace-nowrap">{article.readTime}阅读</span>
            </div>
          </header>

          <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden mb-8 bg-background-100">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose-custom text-foreground-800 leading-relaxed">
            {article.content.split('\n').map((line, i) => {
              if (line.startsWith('## ')) {
                return (
                  <h2 key={i} className="text-xl font-bold text-foreground-950 mt-8 mb-4 font-heading">
                    {line.replace('## ', '')}
                  </h2>
                );
              }
              if (line.startsWith('**') && line.endsWith('**')) {
                return (
                  <p key={i} className="font-semibold text-foreground-950 mt-4 mb-2 text-sm">
                    {line.replace(/\*\*/g, '')}
                  </p>
                );
              }
              if (line.trim() === '') {
                return <div key={i} className="h-3"></div>;
              }
              return (
                <p key={i} className="text-sm text-foreground-700 mb-2 leading-relaxed">
                  {line}
                </p>
              );
            })}
          </div>

          {relatedApps.length > 0 && (
            <section className="mt-12 py-8 border-t border-background-200">
              <h3 className="text-lg font-bold text-foreground-950 mb-5">文中提到的App</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedApps.map((app) => (
                  <div key={app.id} className="flex items-center gap-3 bg-background-100 rounded-xl p-4" data-product-shop="">
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={app.icon} alt={app.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground-950 text-sm whitespace-nowrap">{app.name}</span>
                        <span className="text-xs text-accent-500 whitespace-nowrap">
                          <i className="ri-star-fill text-xs"></i> {app.rating}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-500">{app.downloads} 下载</p>
                    </div>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">{app.price}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {relatedArticles.length > 0 && (
            <section className="mt-8 py-8 border-t border-background-200 mb-12">
              <h3 className="text-lg font-bold text-foreground-950 mb-5">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden hover:shadow-md transition-all duration-200 cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-medium text-foreground-950 text-sm group-hover:text-accent-600 transition-colors line-clamp-2 mb-2">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
    </>
  );
}